<template>
  <div class="signature-makercart-section">
    <div class="signature-items-container">
      <SignatureFIlterSection
        :selected-sort="selectedSort"
        :selected-series="selectedSeries"
        :selected-locale="selectedLocale"
        @update:selected-sort="onSortSelectChange"
        @update:selected-series="onSeriesSelectChange"
        @update:selected-locale="onLocaleSelectChange"
      />

      <div v-if="isLoading" class="loading-state">
        <div class="spinner-container">
          <div class="mt-5 spinner"></div>
        </div>
      </div>

      <div v-else>
        <ErrorState
          v-if="error"
          title="Failed to Load Signature Makercart Activities"
          :message="error"
          :error-code="errorCode"
        />

        <div v-else class="signature-item-list">
          <div v-if="signatureItems.length === 0" class="no-items-message">
            <ErrorState
              state="notFound"
              :title="emptyStateTitle"
              :message="notFoundMessage"
            />
          </div>
          <div v-else class="signature-item-grid-container">
            <SignatureItemMakerCart
              v-for="item in signatureItems"
              :key="item.id"
              :item="item"
              :is-logged="isUserLogged"
              @update-liked-status="updateLikedStatus"
              @item-clicked="handleItemClicked(item.id)"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="totalPages > 1 && !error" class="pagination-controls mb-5">
      <img
        src="/images/chinaus/vector-right.svg"
        class="mr-2"
        alt=""
        @click="changePage(currentPage - 1)"
        @keydown.enter="changePage(currentPage - 1)"
        @keydown.space.prevent="changePage(currentPage - 1)"
        role="button"
        tabindex="0"
        aria-label="Go to previous page"
      />

      <button
        v-for="page in visiblePages"
        :key="page"
        :class="{ 'page-active': page === currentPage }"
        @click="changePage(page)"
      >
        {{ page }}
      </button>

      <img
        src="/images/chinaus/vector-left.svg"
        class="ml-2"
        alt=""
        @click="changePage(currentPage + 1)"
        @keydown.enter="changePage(currentPage + 1)"
        @keydown.space.prevent="changePage(currentPage + 1)"
        role="button"
        tabindex="0"
        aria-label="Go to next page"
      />
    </div>

    <!-- Signature Makercart Detail Modal -->
    <SignatureMakercartDetailModal
      :visible="showModal"
      :makercart-id="selectedMakercartId"
      @close="closeModal"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import SignatureItemMakerCart from "./SignatureItemMakerCart.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import SignatureFIlterSection from "./SignatureFIlterSection.vue";
import SignatureMakercartDetailModal from "./SignatureMakercartDetailModal.vue";
import { useSignatureMakercart } from "./useSignatureMakercart.js";
import { watch } from "vue";

const props = defineProps({
  searchQuery: {
    type: String,
    default: "",
  },
  selectedChips: {
    type: Array,
    default: () => [],
  },
  isUserLogged: {
    type: Boolean,
    default: false,
  },
  selectedSort: {
    type: String,
    default: "newest",
  },
  activeLocale: {
    type: String,
    default: "US",
  },
});

const emit = defineEmits([
  "update:searchQuery",
  "update:selectedSort",
  "update:selectedLocale",
  "search",
  "clear-search",
  "locale-changed",
  "cart-clicked",
  "clear-filter",
  "clear-all-filters",
  "sort-changed",
  "update-liked-status",
  "item-clicked",
  "page-changed",
  "modal-state-changed",
]);

// Use the composable
const {
  selectedSort,
  selectedSeries,
  selectedLocale,
  signatureItems,
  isLoading,
  error,
  errorCode,
  totalPages,
  currentPage,
  showModal,
  selectedMakercartId,
  emptyStateTitle,
  notFoundMessage,
  visiblePages,
  isSignatureModalOpen,
  updateLikedStatus: updateLikedStatusComposable,
  handleItemClicked: handleItemClickedComposable,
  closeModal,
  changePage: changePageComposable,
  onSortSelectChange,
  onSeriesSelectChange,
  onLocaleSelectChange,
} = useSignatureMakercart(props, emit);

// Watch for modal state changes and emit to parent
watch(isSignatureModalOpen, (isOpen) => {
  emit("modal-state-changed", isOpen);
});

// Wrapper functions to emit events
const updateLikedStatus = (data) => {
  updateLikedStatusComposable(data);
  emit("update-liked-status", data);
};

const handleItemClicked = (makercartId) => {
  handleItemClickedComposable(makercartId);
  emit("item-clicked", makercartId);
};

const changePage = (page) => {
  changePageComposable(page);
  emit("page-changed", page);
};
</script>

<style scoped>
.signature-makercart-section {
  width: 100%;
}

.signature-items-container {
  width: 100%;
  margin-top: 20px;
}

.signature-item-list {
  flex-wrap: wrap;
  padding: 20px 0px;
  min-height: 70vh;
  justify-content: center;
}

.signature-item-grid-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.no-items-message {
  text-align: center;
  width: 100%;
  padding: 20px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
}

.pagination-controls button {
  padding: 8px 12px;
  border: 1px solid #d8dae5;
  background: #ffffff;
  border-radius: 6px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 500;
  color: #475467;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-controls button:hover {
  background: #f2f3fb;
  border-color: var(--primary-blue);
}

.pagination-controls button.page-active {
  background: var(--primary-blue);
  color: #ffffff;
  border-color: var(--primary-blue);
}

.pagination-controls img {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.pagination-controls img:hover {
  opacity: 0.7;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .signature-item-grid-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .signature-item-grid-container {
    grid-template-columns: 1fr;
  }

  .signature-items-container {
    margin-top: 16px;
  }

  .signature-item-list {
    padding: 0px;
  }
}

@media (max-width: 480px) {
  .signature-item-grid-container {
    gap: 30px;
  }

  .pagination-controls {
    margin-top: 24px;
  }

  .pagination-controls button {
    padding: 6px 10px;
    font-size: 12px;
  }
}
</style>
